/* eslint-disable react/require-default-props */
/* eslint-disable arrow-body-style */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { Accordion, SkeletonLoading } from '@saleshandy/design-system';
import {
  ChevronDown,
  ChevronUp,
  Filter,
  FilterSearch,
  Lock,
  Minus,
  Plus,
} from '@saleshandy/icons';
import { useOverlayScrollbars } from 'overlayscrollbars-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import 'overlayscrollbars/styles/overlayscrollbars.css';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import Button from '../../../../shared/design-system/components/atoms/button';
import Tag from '../../../../shared/design-system/components/tag';
import { FilterComponentType, LeadFinderSecondaryTab } from '../../types';
import DropDownFilterComponent from './v2-dropdown-filter';
import V2FilterCheckbox from './checkbox-filter';
import KeywordInputComponent from './keyword-search-input/keyword-search-input';
import SavedFilter from './saved-filter/saved-filter';
import RadioFilter from './v2-radio-filter';
import RadioButtonGroup from '../../../../shared/design-system/components/radio-button-group';
import { Placement } from '../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import Input from '../../../../shared/design-system/components/input';
import Autocomplete from './search-auto-complete/search-auto-complete';
import CompanyListFilter from './company-list-filter/company-list-filter';
import CompanyFilterBulkSearch from './company-filter-bulk-search/company-filter-bulk-search';
import GroupDropdownFilter from './group-dropdown-filter';
import GroupDropdownWithSuggestionsFilter from './group-dropdown-with-suggestions-filter';
import {
  COMPANY_FILTER_GROUP_ORDER,
  COMPANY_FILTER_LIST,
} from '../../helpers/company-filter-config';
import { SavedListData } from './v2-saved-tab';
import TruncatedTextWithAutoTooltip from '../../../../shared/components/truncated-text-with-auto-tooltip';
import {
  isListFilter,
  RESTRICTED_COMPANY_FILTER_IDS,
} from '../../helpers/helper';
import { RootState } from '../../../../store/root-reducer';
import UpgradeFilterModal from './upgrade-filter-modal';

interface V2CompanyFilterProps {
  handleRecentSearch?: () => void;
  handleGeneratePayload: (source?: string | URLSearchParams) => any;
  setSelectedCompanies: React.Dispatch<React.SetStateAction<number[]>>;
  handleDeleteFilter: (filter: any, applyFilters?: boolean) => boolean;
  handleGetAllKeysByFilterFromQuery: (filter: any) => Set<any>;
  handleIndividualTag: (tag: any) => void;
  handleQuery: (params: {
    filterKey: string;
    filterValue: any;
    filterType: string;
    filterExcludeKey?: string;
    showExactMatch?: boolean;
    showKnownUnknown?: number;
    onKeyEnter?: boolean;
    onFiltersUpdated?: (updatedFilters: URLSearchParams) => void;
    baseFilters?: URLSearchParams;
  }) => void;
  searchCompanies: (payload?: any, performSearch?: boolean) => void;
  isLoading: boolean;
  keywordInputRef: React.RefObject<HTMLInputElement>;
  activeSubTab: LeadFinderSecondaryTab;
  applyPendingFilters: (
    filtersToApply?: URLSearchParams,
    options?: { skipUrlPush?: boolean },
  ) => void;
  hasPendingFilters: () => boolean;
  isPartialFilterApplied?: boolean;
  clearPendingFilters: () => void;
  pendingFilters: URLSearchParams;
  skipUrlPushForBulkSearch?: boolean;
  onClearAll?: () => void;
  companyListsData?: SavedListData[];
  isLoadingLists?: boolean;
  signalsListData?: Array<{ key: string; value: string }>;
  newsListData?: Array<{ key: string; value: string }>;
  companyIdNameMap?: Record<string, string>;
  setCompanyIdNameMap?: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setSkipUrlPushForBulkSearch?: (skip: boolean) => void;
  skipUrlPushForBulkSearchRef?: React.MutableRefObject<boolean>;
}

export default function V2CompanyFilter({
  handleRecentSearch = () => {},
  handleGeneratePayload,
  setSelectedCompanies,
  handleDeleteFilter,
  handleGetAllKeysByFilterFromQuery,
  handleIndividualTag,
  handleQuery,
  searchCompanies,
  isLoading,
  keywordInputRef,
  activeSubTab,
  applyPendingFilters,
  hasPendingFilters,
  isPartialFilterApplied = false,
  clearPendingFilters,
  pendingFilters,
  skipUrlPushForBulkSearch = false,
  onClearAll,
  companyListsData = [],
  isLoadingLists = false,
  signalsListData = [],
  newsListData = [],
  companyIdNameMap,
  setCompanyIdNameMap,
  setSkipUrlPushForBulkSearch,
  skipUrlPushForBulkSearchRef,
}: V2CompanyFilterProps) {
  const isFilterRestrictionEnabled = useSelector(
    (state: RootState) => state?.home?.leadFinder?.isFilterRestrictionEnabled,
  );

  const [selectedFilter, setSelectedFilter] = useState<any>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(COMPANY_FILTER_GROUP_ORDER),
  );
  const [showFilterSearch, setShowFilterSearch] = useState(false);
  const [filterSearchQuery, setFilterSearchQuery] = useState('');
  const [knownUnknownValues, setKnownUnknownValues] = useState<
    Record<string, number | null>
  >({});
  const [bulkSearchState, setBulkSearchState] = useState<{
    show: boolean;
    focusKey?: string;
  }>({ show: false, focusKey: undefined });
  const [bulkFilterSyncKey, setBulkFilterSyncKey] = useState<
    Record<string, boolean>
  >({});
  const overlayRef = useRef(null);
  const filterSearchInputRef = useRef<HTMLInputElement>(null);
  const accordionKey = 3831;

  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);

  const [showTagTooltip, setShowTagTooltip] = useState<any>(null);

  const [initialize] = useOverlayScrollbars({
    options: {
      overflow: {
        x: 'hidden',
      },
      scrollbars: {
        autoHide: 'leave',
        autoHideDelay: 500,
      },
    },
    defer: true,
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleGeneratePayloadForSavedFilter = useCallback(() => {
    const urlEmpty = !(location?.search || '').trim();
    if (skipUrlPushForBulkSearch && urlEmpty && pendingFilters.toString()) {
      return handleGeneratePayload(
        new URLSearchParams(pendingFilters.toString()),
      );
    }
    return handleGeneratePayload();
  }, [
    handleGeneratePayload,
    skipUrlPushForBulkSearch,
    location.search,
    pendingFilters,
  ]);

  const onSelectFilter = (id, filter) => {
    setSelectedFilter((prevSelectedFilter) => {
      const index = prevSelectedFilter.findIndex((f) => f.id === id);
      if (index === -1) {
        return [
          ...prevSelectedFilter,
          {
            id,
            filter,
          },
        ];
      }
      return prevSelectedFilter.filter((f) => f.id !== id);
    });
  };

  const onToggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const isSelectedFilter = useMemo(() => {
    return (filter) => selectedFilter.some((f) => f.id === filter.id);
  }, [selectedFilter]);

  // Group filters by their group property
  const groupedFilters = useMemo(() => {
    const groups: Record<string, any[]> = {};

    // Exclude Company List filter when in Net New sub-tab for Company
    const effectiveCompanyFilterList = COMPANY_FILTER_LIST.filter(
      (filter: any) =>
        !(
          filter?.id === 'filter-company-list' &&
          activeSubTab === LeadFinderSecondaryTab.NEW
        ),
    );

    effectiveCompanyFilterList.forEach((filter: any) => {
      const groupName = filter.group || 'Other';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(filter);
    });

    // Return groups in the specified order
    return COMPANY_FILTER_GROUP_ORDER.filter(
      (groupName) => groups[groupName] && groups[groupName].length > 0,
    ).map((groupName) => ({
      name: groupName,
      filters: groups[groupName],
    }));
  }, [activeSubTab]);

  const filteredGroupedFilters = useMemo(() => {
    const query = filterSearchQuery.trim().toLowerCase();
    if (!query) return groupedFilters;

    return groupedFilters
      .map((group) => {
        const groupMatches = group.name.toLowerCase().includes(query);
        const matchingFilters = group.filters.filter(
          (f: any) =>
            groupMatches || (f?.label && f.label.toLowerCase().includes(query)),
        );
        return { ...group, filters: matchingFilters };
      })
      .filter((group) => group.filters.length > 0);
  }, [groupedFilters, filterSearchQuery]);

  const appliedGroupCount = useMemo(() => {
    return groupedFilters.reduce((total: number, group: any) => {
      const groupTotal = group.filters.reduce((sum: number, filter: any) => {
        const appliedCount = handleGetAllKeysByFilterFromQuery(filter).size;
        return sum + (appliedCount > 0 ? 1 : 0);
      }, 0);
      return total + groupTotal;
    }, 0);
  }, [
    groupedFilters,
    pendingFilters,
    location?.search,
    handleGetAllKeysByFilterFromQuery,
  ]);

  const handleClearAll = () => {
    handleRecentSearch?.();

    // Clear pending filters immediately
    if (clearPendingFilters) {
      clearPendingFilters();
    }

    // Clear URL params (company tab doesn't have the same recent-search clearing hook)
    history.push({ search: '' });

    // Clear local state
    setSelectedFilter([]);
    setSelectedCompanies([]);

    onClearAll?.();
  };

  const prevSkipUrlPushForBulkSearchRef = useRef(skipUrlPushForBulkSearch);
  useEffect(() => {
    const wasBulk = prevSkipUrlPushForBulkSearchRef.current;
    const isBulk = skipUrlPushForBulkSearch;
    prevSkipUrlPushForBulkSearchRef.current = isBulk;

    if (!isBulk || wasBulk) return;
    if (!location?.search) return;

    history.replace({ search: '' });
  }, [skipUrlPushForBulkSearch, location?.search, history]);

  useEffect(() => {
    initialize(overlayRef.current);
  }, [initialize]);

  useEffect(() => {
    if (showFilterSearch && filterSearchInputRef.current) {
      filterSearchInputRef.current.focus();
    }
  }, [showFilterSearch]);

  useEffect(() => {
    if (keywordInputRef.current) {
      keywordInputRef.current.focus();
    }
    return () => {
      setSelectedFilter([]);
    };
  }, []);

  // Sync knownUnknownValues from URL params
  useEffect(() => {
    const newValues: Record<string, number | null> = {};
    COMPANY_FILTER_LIST.forEach((filter) => {
      filter?.type?.forEach((subFilter) => {
        if (subFilter?.advanceFilters?.showKnownUnknownRadio) {
          const isUnknownParam = queryParams.get(`${subFilter.key}_isUnknown`);
          if (isUnknownParam === '1') {
            newValues[subFilter.key] = 1;
          } else if (isUnknownParam === '0') {
            newValues[subFilter.key] = 0;
          } else {
            newValues[subFilter.key] = null;
          }
        }
      });
    });
    setKnownUnknownValues(newValues);
  }, [location?.search]);

  const getLebelTooltip = (text: string) => (
    <Tooltip id="popover-basic" className="bs-tooltip-inner">
      {text}
    </Tooltip>
  );

  const renderFilterAppliedTags = (allTags: Set<any>, filter: any) => {
    const tagsArray = Array.from(allTags) as any[];
    const includeTags = tagsArray.filter(
      (tag: any) => !tag?.isFlag && !tag?.isExclude,
    );
    const excludeTags = tagsArray.filter(
      (tag: any) => !tag?.isFlag && tag?.isExclude,
    );
    const exactMatchTags = tagsArray.filter(
      (tag: any) => tag?.isFlag && tag?.flagType === 'exactMatch',
    );
    const unknownKnownTags = tagsArray.filter(
      (tag: any) => tag?.isFlag && tag?.flagType === 'isUnknown',
    );
    const renderTagChip = (tag: any, idx: number, className: string) => {
      const displayLabel = tag?.label;
      const truncatedLabel =
        displayLabel?.length > 15
          ? `${displayLabel.substring(0, 15)}..`
          : displayLabel;
      if (tag?.isLoading) {
        return (
          <SkeletonLoading className="filter-tag" width={100} height={20} />
        );
      }
      return (
        <span
          key={`${tag?.key}-${tag?.label}`}
          onMouseEnter={() =>
            setShowTagTooltip(`tag-${tag?.key}-${tag?.label || ''}-${idx}`)
          }
          onMouseLeave={() => setShowTagTooltip(null)}
        >
          <OverlayTrigger
            placement={Placement.BottomStart}
            overlay={getLebelTooltip(displayLabel)}
            show={
              showTagTooltip === `tag-${tag?.key}-${tag?.label || ''}-${idx}` &&
              displayLabel?.length > 15
            }
          >
            <Tag
              value={truncatedLabel}
              variant={Tag.Variant.Remove}
              className={className}
              onClick={(e) => {
                e?.stopPropagation();
                handleIndividualTag(tag);
              }}
            />
          </OverlayTrigger>
        </span>
      );
    };
    return (
      <>
        {includeTags.length > 0 && (
          <div className="filter-section filter-section-includes">
            {excludeTags.length > 0 && (
              <span className="filter-section-label">
                Include {isListFilter(filter?.key) && 'Lists'}
              </span>
            )}
            <div className="filter-section-chips">
              {includeTags
                .slice(0, 3)
                .map((tag, idx) =>
                  renderTagChip(tag, idx, 'filter-tag filter-tag-include'),
                )}
              {includeTags.length > 3 && (
                <Tag
                  value={`+${includeTags.length - 3}`}
                  className="filter-tag more-tag"
                />
              )}
            </div>
          </div>
        )}
        {excludeTags.length > 0 && (
          <div className="filter-section filter-section-excludes">
            <span className="filter-section-label">
              Exclude {isListFilter(filter?.key) && 'Lists'}
            </span>
            <div className="filter-section-chips">
              {excludeTags
                .slice(0, 3)
                .map((tag, idx) =>
                  renderTagChip(tag, idx, 'filter-tag filter-tag-exclude'),
                )}
              {excludeTags.length > 3 && (
                <Tag
                  value={`+${excludeTags.length - 3}`}
                  className="filter-tag more-tag"
                />
              )}
            </div>
          </div>
        )}
        {(exactMatchTags.length > 0 || unknownKnownTags.length > 0) && (
          <div className="filter-section filter-section-advance">
            <span className="filter-section-label">Advance Filter</span>
            <div className="filter-section-chips">
              {exactMatchTags.map((tag: any) =>
                renderTagChip(tag, 0, 'filter-tag filter-tag-flag'),
              )}
              {unknownKnownTags.map((tag: any) =>
                renderTagChip(tag, 0, 'filter-tag filter-tag-flag'),
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderFilterPanelContent = (filter: any) => {
    const lockedSignals = [
      'New Executive Hires in GTM',
      'Scaling Product & Engineering',
      'Freshly Funded & Hiring Fast',
    ];
    const lockedNews = ['Expansion', 'Investment', 'Risk / Negative Events'];
    const matchLocked = (val: string | undefined, list: string[]) =>
      !!val &&
      list.some(
        (lk) =>
          String(val).trim().toLowerCase() === String(lk).trim().toLowerCase(),
      );
    const mapWithLocked = (items: any[], lockedList: string[]) =>
      (items || []).map((item: any) => {
        const cloned = { ...item };
        if (
          isFilterRestrictionEnabled === 1 &&
          (matchLocked(cloned.value, lockedList) ||
            matchLocked(cloned.label, lockedList) ||
            matchLocked(cloned.key, lockedList))
        ) {
          cloned.isLocked = true;
        }
        if (Array.isArray(cloned.children) && cloned.children.length > 0) {
          cloned.children = cloned.children.map((c: any) => ({
            ...c,
            isLocked:
              isFilterRestrictionEnabled === 1 &&
              (matchLocked(c.value, lockedList) ||
                matchLocked(c.label, lockedList) ||
                matchLocked(c.key, lockedList)),
          }));
        }
        return cloned;
      });

    return (
      <>
        {filter?.type?.map((subFilter) => (
          <React.Fragment key={`subfilter-${filter?.id}-${subFilter.id}`}>
            {subFilter?.value === FilterComponentType.MULTILEVEL_CHECKBOX && (
              <>
                {(() => {
                  const effectiveSubFilter =
                    subFilter.key === 'signals' || subFilter.key === 'news'
                      ? {
                          ...subFilter,
                          list:
                            subFilter.key === 'signals'
                              ? mapWithLocked(
                                  signalsListData || [],
                                  lockedSignals,
                                )
                              : mapWithLocked(newsListData || [], lockedNews),
                        }
                      : subFilter;
                  return (
                    <V2FilterCheckbox
                      key={`filter-subfilter-${filter?.id}-${
                        effectiveSubFilter.id
                      }-${effectiveSubFilter.list?.length || 0}`}
                      subFilter={effectiveSubFilter}
                      searchLeads={searchCompanies}
                      hideIncludeExclude={
                        effectiveSubFilter?.hideIncludeExclude ||
                        knownUnknownValues[effectiveSubFilter.key] === 1
                      }
                      handleQuery={handleQuery}
                      isExpand={isSelectedFilter(filter)}
                      parentKey={filter?.key}
                      bulkFilterSyncKey={bulkFilterSyncKey}
                      pendingFilters={pendingFilters}
                      applyPendingFilters={applyPendingFilters}
                      isDisabledByUnknown={
                        knownUnknownValues[effectiveSubFilter.key] === 1
                      }
                      companyIdNameMap={companyIdNameMap}
                      setCompanyIdNameMap={setCompanyIdNameMap}
                      onLockedClick={() => setShowUpgradeModal(true)}
                    />
                  );
                })()}
                {subFilter?.enableBulkSearch && (
                  <Button
                    variant={'link' as any}
                    className="mt-2 mb-0 p-0 blue-txt-11 regular-1 bulk-search-company"
                    onClick={() =>
                      setBulkSearchState({
                        show: true,
                        focusKey: subFilter?.bulkSearchKey || subFilter?.key,
                      })
                    }
                  >
                    + Bulk Search
                  </Button>
                )}
              </>
            )}
            {subFilter?.value === FilterComponentType.COMPANY_LIST && (
              <CompanyListFilter
                includeKey={subFilter?.key}
                excludeKey={subFilter?.excludeKey || 'excludeCompany_list'}
                pendingFilters={pendingFilters}
                handleQuery={handleQuery}
                savedListsData={companyListsData}
                maxTotalLimit={subFilter?.maxTotalLimit}
                isLoadingLists={isLoadingLists}
              />
            )}
            {subFilter?.value === FilterComponentType.SEARCH_INPUT && (
              <>
                <Autocomplete
                  isExpand={isSelectedFilter(filter)}
                  placeholder={subFilter?.placeholder}
                  filterKey={subFilter?.key}
                  name={subFilter?.name}
                  className={subFilter?.className}
                  handleFoundValueExceptFromList={() => ''}
                  handleQuery={handleQuery}
                  applyPendingFilters={applyPendingFilters}
                  advanceFilters={subFilter?.advanceFilters}
                  pendingFilters={pendingFilters}
                />
                {subFilter?.key === 'company_name' &&
                  (() => {
                    const rawVal = pendingFilters.get('company_name');
                    const val =
                      rawVal !== null ? String(rawVal).replace(/"/g, '') : '';
                    if (val.trim().length > 50) {
                      return (
                        <div className="company-name-error mt-1 red-txt-12 regular-1">
                          Company name must be at most 50 characters.
                        </div>
                      );
                    }
                    return null;
                  })()}
                {subFilter?.enableBulkSearch && (
                  <Button
                    variant={'link' as any}
                    className="mt-2 mb-0 p-0 blue-txt-11 regular-1"
                    onClick={() =>
                      setBulkSearchState({
                        show: true,
                        focusKey: subFilter?.bulkSearchKey || subFilter?.key,
                      })
                    }
                  >
                    + Bulk Search
                  </Button>
                )}
              </>
            )}
            {subFilter?.value === FilterComponentType.DROP_DOWN &&
              (() => {
                const isSubFilterLocked =
                  isFilterRestrictionEnabled === 1 &&
                  RESTRICTED_COMPANY_FILTER_IDS.has(subFilter?.id);
                const dependsOnKey = subFilter?.dependsOnKey;
                let isDisabledByDependency = false;
                if (!isSubFilterLocked && dependsOnKey && pendingFilters) {
                  const excludeKeyForDep = `exclude${dependsOnKey
                    .charAt(0)
                    .toUpperCase()}${dependsOnKey.slice(1)}`;
                  const hasInclude =
                    (pendingFilters.getAll(dependsOnKey)?.length ?? 0) > 0;
                  const hasExclude =
                    (pendingFilters.getAll(excludeKeyForDep)?.length ?? 0) > 0;
                  isDisabledByDependency = !hasInclude && !hasExclude;
                }
                return (
                  <DropDownFilterComponent
                    subFilter={subFilter}
                    handleQuery={handleQuery}
                    isDisabledByUnknown={
                      !isSubFilterLocked &&
                      knownUnknownValues[subFilter.key] === 1
                    }
                    isDisabled={isDisabledByDependency}
                    disabledByDependencyTooltip={
                      isDisabledByDependency
                        ? 'Please make a selection in the required filter first'
                        : undefined
                    }
                    pendingFilters={pendingFilters}
                    applyPendingFilters={applyPendingFilters}
                    isLocked={isSubFilterLocked}
                    onLockedClick={() => setShowUpgradeModal(true)}
                  />
                );
              })()}
            {subFilter?.value === FilterComponentType.RADIO_BUTTON && (
              <RadioFilter
                filterKey={subFilter?.key}
                name={subFilter?.name}
                handleQuery={handleQuery}
                hideLabel={subFilter?.hideLabel}
                className={subFilter?.className}
                pendingFilters={pendingFilters}
                customOptions={subFilter?.customOptions}
              />
            )}
            {subFilter?.value === FilterComponentType.GROUP_DROPDOWN &&
              (subFilter?.fetchGroupOptionsFromApi ? (
                <GroupDropdownWithSuggestionsFilter
                  subFilter={subFilter}
                  handleQuery={handleQuery}
                  pendingFilters={pendingFilters}
                  applyPendingFilters={applyPendingFilters}
                  isExpand={isSelectedFilter(filter)}
                />
              ) : (
                <GroupDropdownFilter
                  subFilter={subFilter}
                  handleQuery={handleQuery}
                  pendingFilters={pendingFilters}
                  applyPendingFilters={applyPendingFilters}
                />
              ))}
            {subFilter?.advanceFilters?.showKnownUnknownRadio && (
              <Accordion.Item
                uuid={`${subFilter.key}_advance_settings`}
                className="advance-settings-accordion"
              >
                <Accordion.ItemHeading>
                  <Accordion.ItemButton className="::before:none w-100">
                    <div className="w-100 accordian-label">
                      <span>Advance Filter</span>
                      <div className="d-flex align-items-center justify-content-end">
                        <Accordion.ItemState>
                          {({ expanded }) =>
                            expanded ? (
                              <ChevronUp
                                color="#6B7280"
                                className="accordion-minus-icon"
                              />
                            ) : (
                              <ChevronDown
                                color="#6B7280"
                                className="accordion-plus-icon"
                              />
                            )
                          }
                        </Accordion.ItemState>
                      </div>
                    </div>
                  </Accordion.ItemButton>
                </Accordion.ItemHeading>
                <Accordion.ItemPanel>
                  <div className="known-unknown-filter">
                    <RadioButtonGroup
                      name={`${subFilter.key}_known_unknown`}
                      options={[
                        { label: 'Is Known', value: 0 },
                        { label: 'Is Unknown', value: 1 },
                      ]}
                      value={knownUnknownValues[subFilter.key] ?? null}
                      variant={RadioButtonGroup.Variant.Default}
                      onChange={(value) => {
                        const newValue =
                          value === knownUnknownValues[subFilter.key]
                            ? null
                            : value;
                        setKnownUnknownValues((prev) => ({
                          ...prev,
                          [subFilter.key]: newValue,
                        }));
                        handleQuery({
                          filterKey: `${subFilter.key}_isUnknown`,
                          filterValue: newValue,
                          filterType: FilterComponentType.RADIO_BUTTON,
                          showKnownUnknown: newValue,
                        });
                      }}
                    />
                  </div>
                </Accordion.ItemPanel>
              </Accordion.Item>
            )}
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <div className="v2-lead-filter-root v2-company-filter">
      <div className="v2-lead-filter-header">
        <div className="v2-leads-search">
          <KeywordInputComponent
            activeSubTab={activeSubTab}
            handleQuery={handleQuery}
            keywordInputRef={keywordInputRef}
            hideAISearch={false}
            applyPendingFilters={applyPendingFilters}
          />
        </div>
        <h3 className="v2-leads-filter">
          <div className="d-flex align-items-center gap-8">
            <div>
              <Filter className="sh-filter" />
              Filters
            </div>
            {appliedGroupCount > 0 && (
              <span className="v2-leads-filter-count">{appliedGroupCount}</span>
            )}
          </div>
          <div className="v2-leads-filter-actions">
            {(location?.search || hasPendingFilters?.()) && (
              <>
                <Button
                  variant={'link' as any}
                  onClick={handleClearAll}
                  className="clear-all-button"
                >
                  Clear All
                </Button>
                <div className="divider" />
              </>
            )}
            {activeSubTab !== LeadFinderSecondaryTab.SAVED && (
              <div
                className="v2-leads-filter-search-icon"
                {...accessibleOnClick(() => {
                  setShowFilterSearch((prev) => {
                    if (prev) setFilterSearchQuery('');
                    return !prev;
                  });
                })}
              >
                <FilterSearch width={16} height={16} />
              </div>
            )}
          </div>
        </h3>
      </div>

      {showFilterSearch && activeSubTab !== LeadFinderSecondaryTab.SAVED && (
        <div className="v2-leads-filter-search-bar">
          <Input
            placeholder="Search filter name"
            value={filterSearchQuery}
            onChange={setFilterSearchQuery}
            icons={[{ place: Input.IconPlace.Left, identifier: 'search' }]}
            className="v2-leads-filter-search-input"
            ref={filterSearchInputRef}
          />
        </div>
      )}

      <div className="v2-lead-filters-accordion-container">
        <div
          key={accordionKey}
          ref={overlayRef}
          className="v2-lead-filters-accordion"
        >
          <Accordion
            className="shd-accordion"
            allowMultipleExpanded
            key={accordionKey}
            allowZeroExpanded
          >
            {filteredGroupedFilters.map((group) => {
              const groupSelectedCount = group.filters.reduce(
                (sum: number, filter: any) =>
                  sum + handleGetAllKeysByFilterFromQuery(filter).size,
                0,
              );

              return (
                <Accordion.Item
                  key={group.name}
                  uuid={`filter-group-${group.name.replace(/\s+/g, '-')}`}
                  dangerouslySetExpanded={expandedGroups.has(group.name)}
                  className={classNames('filter-group-accordion', {
                    'filter-group-collapsed': !expandedGroups.has(group.name),
                  })}
                >
                  <Accordion.ItemHeading
                    onClick={() => onToggleGroup(group.name)}
                  >
                    <Accordion.ItemButton className="::before:none w-100">
                      <div className="filter-group-header w-100">
                        <span className="filter-group-title">
                          {group.name}
                          {groupSelectedCount > 0 ? (
                            <span className="filter-group-count">
                              {' '}
                              ({groupSelectedCount})
                            </span>
                          ) : null}
                        </span>
                        <div className="d-flex align-items-center">
                          <Accordion.ItemState>
                            {({ expanded }) =>
                              expanded ? (
                                <ChevronUp
                                  color="#6B7280"
                                  className="accordion-chevron-icon"
                                />
                              ) : (
                                <ChevronDown
                                  color="#6B7280"
                                  className="accordion-chevron-icon"
                                />
                              )
                            }
                          </Accordion.ItemState>
                        </div>
                      </div>
                    </Accordion.ItemButton>
                  </Accordion.ItemHeading>
                  <Accordion.ItemPanel>
                    {expandedGroups.has(group.name) &&
                      group.filters.map((filter: any, index: number) => {
                        const isFilterLocked =
                          isFilterRestrictionEnabled === 1 &&
                          RESTRICTED_COMPANY_FILTER_IDS.has(filter?.id);
                        const allTags = handleGetAllKeysByFilterFromQuery(
                          filter,
                        );
                        const isFirstInGroup = index === 0;
                        return (
                          <Accordion.Item
                            key={filter?.id}
                            uuid={filter?.id}
                            data-filter-id={filter?.id}
                            dangerouslySetExpanded={
                              !isFilterLocked &&
                              selectedFilter?.some((f) => f?.id === filter?.id)
                            }
                            className={classNames([
                              'accordion__item',
                              {
                                'first-in-group': isFirstInGroup,
                              },
                              {
                                accordion__item_selected:
                                  !isFilterLocked && isSelectedFilter(filter),
                              },
                              {
                                accordion__item_locked: isFilterLocked,
                              },
                            ])}
                          >
                            <Accordion.ItemHeading
                              onClick={() => {
                                if (isFilterLocked) {
                                  setShowUpgradeModal(true);
                                } else {
                                  onSelectFilter(filter.id, filter);
                                }
                              }}
                              className={`${
                                allTags.size > 0 && !isSelectedFilter(filter)
                                  ? 'accordion-item-heading-tag'
                                  : 'accordion-item-heading'
                              } ${
                                allTags.size
                                  ? 'accordion-item-selected-tag'
                                  : ''
                              } ${
                                isFilterLocked
                                  ? 'accordion-item-heading-locked'
                                  : ''
                              }`}
                            >
                              <Accordion.ItemButton className="::before:none w-100">
                                <div className="w-100 accordian-label">
                                  <div className="icon">{filter.icon}</div>
                                  <div
                                    className="label"
                                    key={`${filter?.id}-${allTags.size}`}
                                  >
                                    <TruncatedTextWithAutoTooltip
                                      text={filter?.label}
                                    />
                                  </div>
                                  <div className="d-flex align-items-center accordian-label-end">
                                    {!isFilterLocked && allTags.size > 0 && (
                                      <Tag
                                        value={allTags.size}
                                        variant={Tag.Variant.Remove}
                                        className="bg-white rounded-pill"
                                        onClick={(e) => {
                                          e?.stopPropagation();
                                          const shouldApplyFilters =
                                            !!location?.search ||
                                            skipUrlPushForBulkSearch;
                                          const shouldClearAll = handleDeleteFilter(
                                            filter,
                                            shouldApplyFilters,
                                          );
                                          if (
                                            shouldApplyFilters &&
                                            shouldClearAll
                                          ) {
                                            handleClearAll();
                                          }
                                        }}
                                      />
                                    )}
                                    <div className="d-flex align-items-center justify-content-end">
                                      {isFilterLocked ? (
                                        <div
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShowUpgradeModal(true);
                                          }}
                                          role="button"
                                          tabIndex={0}
                                          onKeyPress={(e) => {
                                            e.stopPropagation();
                                            if (
                                              e.key === 'Enter' ||
                                              e.key === ' '
                                            ) {
                                              setShowUpgradeModal(true);
                                            }
                                          }}
                                        >
                                          <Lock
                                            width={16}
                                            height={16}
                                            color="#F59E0B"
                                            className="accordion-lock-icon"
                                          />
                                        </div>
                                      ) : (
                                        <Accordion.ItemState>
                                          {({ expanded }) =>
                                            expanded ? (
                                              <Minus className="accordion-minus-icon" />
                                            ) : (
                                              <Plus className="accordion-plus-icon" />
                                            )
                                          }
                                        </Accordion.ItemState>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {!isFilterLocked &&
                                  allTags.size > 0 &&
                                  !isSelectedFilter(filter) && (
                                    <div className="filter-applied-collapse">
                                      {renderFilterAppliedTags(allTags, filter)}
                                    </div>
                                  )}
                              </Accordion.ItemButton>
                            </Accordion.ItemHeading>

                            <Accordion.ItemPanel>
                              <Accordion.ItemState>
                                {({ expanded: isFilterExpanded }) =>
                                  isFilterExpanded
                                    ? renderFilterPanelContent(filter)
                                    : null
                                }
                              </Accordion.ItemState>
                            </Accordion.ItemPanel>
                          </Accordion.Item>
                        );
                      })}
                  </Accordion.ItemPanel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </div>
      </div>
      {/* Bulk search modal is implemented in a later todo; this state is wired now */}
      <CompanyFilterBulkSearch
        show={bulkSearchState.show}
        focusKey={bulkSearchState.focusKey}
        pendingFilters={pendingFilters}
        onClose={() => setBulkSearchState({ show: false, focusKey: undefined })}
        onSearch={({ filterKey, values, showExactMatch }) => {
          if (location?.search) {
            if (skipUrlPushForBulkSearchRef) {
              skipUrlPushForBulkSearchRef.current = true;
            }
            history.replace({ search: '' });
          }
          setSkipUrlPushForBulkSearch?.(true);
          handleQuery({
            filterKey,
            filterValue: { includedKeys: values, excludedKeys: [] },
            filterType: FilterComponentType.MULTILEVEL_CHECKBOX,
            showExactMatch,
            baseFilters: new URLSearchParams(pendingFilters.toString()),
            onFiltersUpdated: (updatedFilters) => {
              applyPendingFilters(updatedFilters, { skipUrlPush: true });
              setBulkSearchState({ show: false, focusKey: undefined });
              if (values?.length) {
                setBulkFilterSyncKey((prev) => ({
                  ...prev,
                  [filterKey]: true,
                }));
              }
            },
          });
        }}
      />
      <UpgradeFilterModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
      <div className="v2-lead-filter-footer">
        <SavedFilter
          {...{
            handleGeneratePayload: handleGeneratePayloadForSavedFilter,
            isLoading,
            applyPendingFilters,
            hasPendingFilters,
            isPartialFilterApplied,
          }}
        />
      </div>
    </div>
  );
}
